import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./chatHistoryOverview.css";
import { useTranslation } from "react-i18next";
import { guardWrite } from "../utils/permissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faGlobe, faFileAlt, faCircleQuestion, faPlus, faToggleOn, faToggleOff, faSpinner, faUser, faComments, faSearch, faTimes, faLink, faDesktop } from "@fortawesome/free-solid-svg-icons";
import { faFacebookMessenger, faInstagram, faWhatsapp, faTelegram, faSlack } from "@fortawesome/free-brands-svg-icons";
import AddFaqFromHistoryModal from "./AddFaqFromHistoryModal.js";
import SocialDeliveryError from './SocialDeliveryError';
import { Link } from "react-router-dom";
import { buildLiveDeepLink } from "../utils/deepLinks";
import ProductCarousel from './ProductCarousel';

dayjs.extend(utc);
dayjs.extend(timezone);

// Platforms that surface the per-conversation auto-response toggle.
// All social replies use the conversation-scoped reply endpoint.
const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'whatsapp', 'telegram', 'slack'];

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "YYYY-MM-DD" || stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY") {
    return stored;
  }
  return "YYYY-MM-DD";
};

const getUserTimezone = () => {
  return localStorage.getItem("timezone") || "UTC";
};

// Session list + conversation pane. `sessions` are lightweight summaries
// (preview, country, botId — never the messages); the messages for one
// session arrive via `messagesBySession` after the container's lazy fetch,
// triggered through `onSessionSelected`. Paging and search are server-side:
// scrolling the list calls `onLoadMore`, typing calls `onSearchTermChange`.
const ChatHistoryOverview = ({
  sessions,
  messagesBySession = {},
  loadingMessages = {},
  messageErrors = {},
  onSessionSelected,
  onRetryMessages,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  searchTerm = '',
  onSearchTermChange,
  selectedBotId,
}) => {
  const { t } = useTranslation();
  const [selectedSession, setSelectedSession] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [prefilledQuestion, setPrefilledQuestion] = useState("");
  const [prefilledAnswer, setPrefilledAnswer] = useState("");
  const [prefilledBotId, setPrefilledBotId] = useState(null);

  const chatListRef = useRef(null);

  // State for search (the term itself lives in the container — the server filters)
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  // State for manual messaging
  const [replyText, setReplyText] = useState({});
  const [sendingMessage, setSendingMessage] = useState({});

  // State for auto-response toggle
  const [autoResponseEnabled, setAutoResponseEnabled] = useState({});
  const [loadingAutoResponse, setLoadingAutoResponse] = useState({});
  const [deliveryErrors, setDeliveryErrors] = useState({});
  // Social chats whose channel is no longer connected to the agent (session_settings says connected: false):
  // no AI switch and no reply box, which could only fail there.
  const [disconnected, setDisconnected] = useState({});

  // Track which sessions we've already fetched auto-response status for
  const fetchedAutoResponseRef = useRef(new Set());
  const autoResponseRequestVersion = useRef({});
  const autoResponseMutatingRef = useRef(new Set());

  // Cache of visitor profiles keyed by session_id. Populated lazily from
  // /api/messaging_contacts the first time we see a social-platform session.
  // Lets us render real names ("Sarah Bauer") instead of "Telegram User".
  const [contactsBySession, setContactsBySession] = useState({});
  const fetchedContactPlatformsRef = useRef({}); // { facebook: Set<sessionId>, ... }

  // Local messages sent manually (to show immediately before next fetch)
  const [localMessages, setLocalMessages] = useState([]);

  // Replies to ended website chats: per session whether the team can still
  // reply (the widget shows it when the visitor returns within 30 days).
  const [followups, setFollowups] = useState({});

  // Ref for scrolling chat to bottom
  const chatMessagesRef = useRef(null);

  // State for sticky date header
  const [stickyDate, setStickyDate] = useState(null);
  const dateRefs = useRef({});

  // Reset fetched auto-response sessions when bot changes
  useEffect(() => {
    fetchedAutoResponseRef.current = new Set();
    setAutoResponseEnabled({});
    fetchedContactPlatformsRef.current = {};
    setContactsBySession({});
  }, [selectedBotId]);

  // Select a session AND kick off its lazy message load in the container.
  const selectSession = (sessionId) => {
    setSelectedSession(sessionId);
    if (sessionId) onSessionSelected?.(sessionId);
  };

  // Auto-select first session on load or reset if current selection is invalid
  useEffect(() => {
    if (sessions.length > 0) {
      // Check if current selection is still valid
      const isValidSelection = selectedSession && sessions.some(s => s.id === selectedSession);
      if (!isValidSelection) {
        selectSession(sessions[0].id);
      }
    } else {
      setSelectedSession(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, selectedSession]);

  // Scroll to bottom when session changes or messages update
  useEffect(() => {
    if (chatMessagesRef.current) {
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [selectedSession, messagesBySession, localMessages]);

  // Ref for sticky date element
  const stickyDateRef = useRef(null);

  // Handle scroll for sticky date header - stable approach to prevent flickering
  const handleMessagesScroll = () => {
    if (!chatMessagesRef.current) return;

    const container = chatMessagesRef.current;
    const scrollTop = container.scrollTop;

    // Hide sticky date when at the very top to avoid duplicate with first separator
    if (stickyDateRef.current) {
      if (scrollTop < 10) {
        stickyDateRef.current.classList.add('hidden');
      } else {
        stickyDateRef.current.classList.remove('hidden');
      }
    }

    const containerRect = container.getBoundingClientRect();
    const stickyThreshold = containerRect.top + 30;

    // Get all date separators sorted by their position in the DOM
    const dateKeys = Object.keys(dateRefs.current).sort();

    // Find the date separator that is at or above the sticky position
    let currentActiveDate = dateKeys[0] || null;

    for (const dateKey of dateKeys) {
      const element = dateRefs.current[dateKey];
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const separatorCenter = rect.top + rect.height / 2;

      if (separatorCenter <= stickyThreshold) {
        currentActiveDate = dateKey;
      }

      // Hide inline separator when it overlaps with sticky header (but not when at very top)
      const isOverlapping = scrollTop >= 10 && rect.top < stickyThreshold + 5 && rect.bottom > containerRect.top;

      if (isOverlapping) {
        element.classList.add('at-sticky');
      } else {
        element.classList.remove('at-sticky');
      }
    }

    setStickyDate(prev => prev === currentActiveDate ? prev : currentActiveDate);
  };

  // Reset date refs when session changes
  useEffect(() => {
    dateRefs.current = {};
  }, [selectedSession]);

  // Fetch auto-response status when session is selected
  useEffect(() => {
    const fetchAutoResponseStatus = async (sessionId, platform, botId) => {
      if (!SOCIAL_PLATFORMS.includes(platform)) return;
      if (fetchedAutoResponseRef.current.has(sessionId)) return;
      if (!botId) return;

      // Mark as fetched immediately to prevent duplicate requests
      fetchedAutoResponseRef.current.add(sessionId);
      const version = autoResponseRequestVersion.current[sessionId] || 0;

      try {
        setLoadingAutoResponse(prev => ({ ...prev, [sessionId]: true }));
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}session_settings?session_id=${sessionId}&platform=${platform}&bot_id=${botId}`
        );
        if (response.ok) {
          const data = await response.json();
          if (!autoResponseMutatingRef.current.has(sessionId) && version === (autoResponseRequestVersion.current[sessionId] || 0)) {
            setAutoResponseEnabled(prev => ({ ...prev, [sessionId]: data.auto_response_enabled }));
            setDeliveryErrors(prev => ({ ...prev, [sessionId]: data.delivery_error || null }));
            setDisconnected(prev => ({ ...prev, [sessionId]: data.connected === false }));
          }
        }
      } catch (err) {
        console.error('Error fetching auto-response status:', err);
        fetchedAutoResponseRef.current.delete(sessionId); // Allow retry on error
      } finally {
        setLoadingAutoResponse(prev => ({ ...prev, [sessionId]: false }));
      }
    };

    // Fetch auto-response status for all social platform sessions
    sessions.forEach(session => {
      if (SOCIAL_PLATFORMS.includes(session.platform)) {
        fetchAutoResponseStatus(session.id, session.platform, session.botId);
      }
    });
  }, [sessions]);

  // Native Messenger/Instagram takeovers can happen while the inbox is open.
  // Refresh the selected thread; a stale poll must not undo a local toggle/send.
  useEffect(() => {
    const session = sessions.find(s => s.id === selectedSession);
    if (!session || !SOCIAL_PLATFORMS.includes(session.platform)) return;
    const controller = new AbortController();
    const refresh = async () => {
      if (autoResponseMutatingRef.current.has(session.id)) return;
      const version = autoResponseRequestVersion.current[session.id] || 0;
      try {
        const params = new URLSearchParams({ session_id: session.id, platform: session.platform, bot_id: session.botId });
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}session_settings?${params}`, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (!controller.signal.aborted && !autoResponseMutatingRef.current.has(session.id) && version === (autoResponseRequestVersion.current[session.id] || 0)) {
          setAutoResponseEnabled(prev => ({ ...prev, [session.id]: data.auto_response_enabled }));
          setDeliveryErrors(prev => ({ ...prev, [session.id]: data.delivery_error || null }));
          setDisconnected(prev => ({ ...prev, [session.id]: data.connected === false }));
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Could not refresh AI status', error);
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedSession, sessions]);

  // Batch-fetch visitor profiles for social-platform sessions so we can show
  // real names instead of "Telegram User" / "Facebook User" / "Instagram User".
  // /api/messaging_contacts only accepts facebook | instagram | telegram.
  useEffect(() => {
    const CONTACT_PLATFORMS = ['facebook', 'instagram', 'telegram', 'slack'];

    CONTACT_PLATFORMS.forEach(platform => {
      const wantedIds = sessions
        .filter(s => s.platform === platform)
        .map(s => s.id);
      if (wantedIds.length === 0) return;

      const seen = fetchedContactPlatformsRef.current[platform] || new Set();
      const toFetch = wantedIds.filter(id => !seen.has(id));
      if (toFetch.length === 0) return;

      // Mark as fetched immediately so re-renders don't queue duplicates.
      const nextSeen = new Set(seen);
      toFetch.forEach(id => nextSeen.add(id));
      fetchedContactPlatformsRef.current[platform] = nextSeen;

      const url = `${process.env.REACT_APP_API_BASE_URL}messaging_contacts?platform=${platform}&session_ids=${encodeURIComponent(toFetch.join(','))}`;
      fetch(url)
        .then(r => (r.ok ? r.json() : []))
        .then(rows => {
          if (!Array.isArray(rows) || rows.length === 0) return;
          setContactsBySession(prev => {
            const next = { ...prev };
            for (const c of rows) {
              if (c.session_id) next[c.session_id] = c;
            }
            return next;
          });
        })
        .catch(err => {
          console.error(`messaging_contacts fetch failed for ${platform}:`, err);
          // Allow retry on next render if the request actually failed.
          toFetch.forEach(id => nextSeen.delete(id));
        });
    });
  }, [sessions]);

  // Handle scroll for lazy loading more sessions (next page from the server)
  const handleChatListScroll = () => {
    if (!chatListRef.current || loadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = chatListRef.current;
    // Ask the container for the next page when within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      onLoadMore?.();
    }
  };

  const handleToggleSources = (key) =>
    setExpandedSources((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  const handleToggleAutoResponse = async (sessionId, platform, currentStatus) => {
    if (!guardWrite()) return;
    autoResponseRequestVersion.current[sessionId] = (autoResponseRequestVersion.current[sessionId] || 0) + 1;
    autoResponseMutatingRef.current.add(sessionId);
    try {
      setLoadingAutoResponse(prev => ({ ...prev, [sessionId]: true }));

      const botId = sessions.find(s => s.id === sessionId)?.botId || null;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}session_settings/auto_response`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          platform: platform,
          auto_response_enabled: !currentStatus,
          bot_id: botId,
        }),
      });

      if (response.ok) {
        setAutoResponseEnabled(prev => ({ ...prev, [sessionId]: !currentStatus }));
      } else {
        const result = await response.json();
        alert(result.detail || t('failed_to_update_settings'));
      }
    } catch (err) {
      console.error('Error toggling auto-response:', err);
    } finally {
      autoResponseMutatingRef.current.delete(sessionId);
      autoResponseRequestVersion.current[sessionId] = (autoResponseRequestVersion.current[sessionId] || 0) + 1;
      setLoadingAutoResponse(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  // Website chats: may the team still reply, and has the visitor seen it?
  const loadFollowup = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}live/followup/${encodeURIComponent(sessionId)}`);
      const data = response.ok ? await response.json() : null;
      setFollowups(prev => ({ ...prev, [sessionId]: data }));
    } catch (err) {
      setFollowups(prev => ({ ...prev, [sessionId]: null }));
    }
  };

  useEffect(() => {
    const session = sessions.find(s => s.id === selectedSession);
    if (!session || SOCIAL_PLATFORMS.includes(session.platform) || session.channel === 'portal') return;
    loadFollowup(session.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession]);

  const handleSendFollowup = async (sessionId) => {
    if (!guardWrite()) return;
    const message = replyText[sessionId]?.replace(/\r/g, '').trim();
    if (!message) return;
    setSendingMessage(prev => ({ ...prev, [sessionId]: true }));
    try {
      const key = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}${Math.random().toString(36).slice(2)}`;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}live/followup/${encodeURIComponent(sessionId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
        body: JSON.stringify({ content: message }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        const sessionData = sessions.find(s => s.id === sessionId);
        setLocalMessages(prev => [...prev, {
          session_id: sessionId,
          bot_id: sessionData?.botId || null,
          type: 'agent',
          content: message,
          timestamp: new Date().toISOString(),
          platform: sessionData?.platform,
          bot_name: sessionData?.botName || t('bot_name_fallback'),
          isLocal: true,
        }]);
        setReplyText(prev => ({ ...prev, [sessionId]: '' }));
        loadFollowup(sessionId);
      } else {
        alert(result?.detail?.message || t('failed_to_send_message') || 'Failed to send message');
        loadFollowup(sessionId);
      }
    } catch (err) {
      console.error('Error sending follow-up:', err);
      alert(t('error_sending_message') || 'Error sending message');
    } finally {
      setSendingMessage(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const formatFollowupDate = (iso) => (iso
    ? new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`).toLocaleDateString()
    : '');

  const handleSendMessage = async (sessionId, platform) => {
    if (!guardWrite()) return;
    // Normalize whitespace: remove carriage returns, collapse multiple spaces, trim
    const message = replyText[sessionId]
      ?.replace(/\r\n/g, '\n')  // Windows line endings to Unix
      ?.replace(/\r/g, '')      // Remove any remaining carriage returns
      ?.replace(/  +/g, ' ')    // Collapse multiple spaces to single space
      ?.trim();
    if (!message) return;

    try {
      autoResponseMutatingRef.current.add(sessionId);
      setSendingMessage(prev => ({ ...prev, [sessionId]: true }));

      const sessionData = sessions.find(s => s.id === sessionId);
      const botId = sessionData?.botId || null;
      if (!botId) {
        alert(t('no_bot_for_session') || 'No bot found for this session.');
        return;
      }
      autoResponseRequestVersion.current[sessionId] = (autoResponseRequestVersion.current[sessionId] || 0) + 1;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}messaging/conversations/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, platform, bot_id: botId, message }),
      });
      const result = await response.json();
      setDeliveryErrors(prev => ({ ...prev, [sessionId]: result.delivery_error || null }));
      autoResponseRequestVersion.current[sessionId] = (autoResponseRequestVersion.current[sessionId] || 0) + 1;
      if (typeof result.auto_response_enabled === 'boolean') {
        setAutoResponseEnabled(prev => ({ ...prev, [sessionId]: result.auto_response_enabled }));
      }

      if (response.ok) {
        // Add message to local state to show immediately
        const newMessage = {
          session_id: sessionId,
          bot_id: botId,
          type: 'agent',
          content: message,
          timestamp: new Date().toISOString(),
          platform: platform,
          bot_name: sessionData?.botName || t('bot_name_fallback'),
          isLocal: true, // Mark as local message to skip ReactMarkdown
        };
        setLocalMessages(prev => [...prev, newMessage]);
        setReplyText(prev => ({ ...prev, [sessionId]: '' }));
      } else {
        alert(result.detail || t('failed_to_send_message') || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert(t('error_sending_message') || 'Error sending message');
    } finally {
      autoResponseMutatingRef.current.delete(sessionId);
      setSendingMessage(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const LinkRenderer = (props) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  );

  const openFaqModal = (question, answer, botId) => {
    setPrefilledQuestion(question);
    setPrefilledAnswer(answer);
    setPrefilledBotId(botId);
    setFaqModalOpen(true);
  };

  // Normalize the various website-y platform values ('voice', 'live', '')
  // into one canonical key so both the avatar icon and the
  // .chat-avatar.<platform> class line up. A chat inside the portal (the
  // server's 'portal' bucket, session.channel) shows as the portal.
  const getDisplayPlatform = (platform, channel) => {
    if (channel === 'portal' || platform === 'internal') return 'portal';
    if (!platform || ['website', 'voice', 'live'].includes(platform)) {
      return 'website';
    }
    return platform;
  };

  const getPlatformIcon = (platform, channel) => {
    switch (getDisplayPlatform(platform, channel)) {
      case 'facebook': return faFacebookMessenger;
      case 'instagram': return faInstagram;
      case 'whatsapp': return faWhatsapp;
      case 'telegram': return faTelegram;
      case 'slack': return faSlack;
      case 'chat_link': return faLink;
      case 'portal': return faDesktop;
      case 'website': return faGlobe;
      default: return faUser;
    }
  };

  const getSessionName = (session) => {
    if (!session) return t('unknown');
    const sessionId = session.id;

    // For social platforms we cache the visitor's real profile in
    // contactsBySession (see the messaging_contacts useEffect). Prefer the
    // display name, fall back to @username, then to a generic platform label.
    const contact = contactsBySession[sessionId];
    if (contact) {
      if (contact.display_name) return contact.display_name;
      if (contact.username) return `@${contact.username}`;
    }

    if (sessionId.startsWith('fb_')) return t('session_name_facebook');
    if (sessionId.startsWith('ig_')) return t('session_name_instagram');
    if (sessionId.startsWith('wa_')) return t('session_name_whatsapp', { id: sessionId.replace('wa_', '') });
    if (sessionId.startsWith('tg_')) return t('session_name_telegram');
    if (sessionId.startsWith('sl_')) return t('session_name_slack');
    if (session.platform === 'chat_link') return t('session_name_chat_link');
    if (getDisplayPlatform(session.platform, session.channel) === 'portal') return t('session_name_portal');
    return t('session_name_website');
  };

  // Format date for chat list items using user's date format
  const formatListDate = (dateStr) => {
    return dayjs.utc(dateStr).tz(getUserTimezone()).format(getUserDateFormat());
  };

  // Format date for sticky header and date separators
  const formatDateHeader = (dateStr) => {
    const tz = getUserTimezone();
    const date = dayjs.utc(dateStr).tz(tz).toDate();
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return t('today') || 'Today';
    if (isYesterday) return t('yesterday') || 'Yesterday';

    // Check if same year
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
    }
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Format time for message timestamps (HH:MM)
  const formatMessageTime = (timestamp) => {
    return dayjs.utc(timestamp).tz(getUserTimezone()).format('HH:mm');
  };

  // Get date key for grouping (YYYY-MM-DD)
  const getDateKey = (timestamp) => {
    return dayjs.utc(timestamp).tz(getUserTimezone()).format('YYYY-MM-DD');
  };

  // Get flag image URL from country code using flagcdn.com
  const getCountryFlagUrl = (countryCode, size = 'small') => {
    if (!countryCode || countryCode.length !== 2) return null;
    const code = countryCode.toLowerCase();
    // Use larger images for better quality (2x for retina)
    if (size === 'large') {
      return `https://flagcdn.com/48x36/${code}.png`;
    }
    return `https://flagcdn.com/32x24/${code}.png`;
  };

  // Messages for the currently open session (lazy-loaded by the container,
  // oldest first from the API), merged with anything we just sent manually.
  const currentMessages = messagesBySession[selectedSession] || [];
  const currentSessionChats = [
    ...currentMessages,
    ...localMessages.filter((m) => m.session_id === selectedSession),
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // ASC (oldest first)

  // Clear local messages once a refetched session contains them
  useEffect(() => {
    if (localMessages.length > 0) {
      const localMsgContents = localMessages.map(m => m.content);
      const allInHistory = localMsgContents.every(content =>
        currentMessages.some(c => c.content === content && c.type === 'assistant')
      );
      if (allInHistory) {
        setLocalMessages([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesBySession, localMessages]);

  // Set initial sticky date when session changes or messages load
  useEffect(() => {
    // Set initial sticky date after a brief delay to allow refs to be set
    const timer = setTimeout(() => {
      if (selectedSession) {
        const sessionChats = currentSessionChats;

        if (sessionChats.length > 0) {
          // Always start with the LAST date (since we scroll to bottom)
          const lastDateKey = getDateKey(sessionChats[sessionChats.length - 1].timestamp);
          setStickyDate(lastDateKey);

          // Then trigger a scroll check to set the correct date
          if (chatMessagesRef.current) {
            handleMessagesScroll();
          }
        } else {
          setStickyDate(null);
        }
      } else {
        setStickyDate(null);
      }
    }, 150);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession, messagesBySession]);

  const renderChatMessages = () => {
    if (!selectedSession) return null;

    const sessionChats = currentSessionChats; // already ASC (oldest first)

    if (sessionChats.length === 0) {
      return <p className="chat-empty-message">{t("no_messages_in_this_session")}</p>;
    }

    let lastDateKey = null;
    const elements = [];

    sessionChats.forEach((chat, index) => {
      const sourceKey = `${selectedSession}-${index}`;
      const hasWeb = chat.sources?.website_links?.length > 0;
      const hasDocs = chat.sources?.documents?.length > 0;
      const hasFaq = chat.sources?.faq?.length > 0;
      const currentDateKey = getDateKey(chat.timestamp);

      // Add date separator if this is a new day
      if (currentDateKey !== lastDateKey) {
        elements.push(
          <div
            key={`date-${currentDateKey}`}
            className="chat-date-separator"
            ref={(el) => { dateRefs.current[currentDateKey] = el; }}
            data-date={currentDateKey}
          >
            <span className="chat-date-separator-text">{formatDateHeader(chat.timestamp)}</span>
          </div>
        );
        lastDateKey = currentDateKey;
      }

      elements.push(
        <div
          key={index}
          className={`chat-message ${
            chat.type === "user_input" ? "user-history-message" : "assistant-message"
          }`}
        >
          {chat.type === "user_input" ? (
            <div className="user-message-group">
              <div className="user-message-body">
                <button
                  className="add-faq-button"
                  title={t('add_faq_button_title')}
                  onClick={() => {
                    const nextMessage = sessionChats.find(c =>
                      c.type !== 'user_input' && c.timestamp > chat.timestamp
                    );
                    openFaqModal(chat.content, nextMessage ? nextMessage.content : '', chat.bot_id);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t('success_faq_action')}
                </button>

                <div className="message-bubble-history">
                  <ReactMarkdown
                    components={{ a: LinkRenderer }}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {chat.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                  </ReactMarkdown>
                </div>
              </div>

              <span className="message-time" style={{marginTop: 0}}>{formatMessageTime(chat.timestamp)}</span>
            </div>
          ) : (
            <>
              <div className="message-bubble-history">
                <ReactMarkdown
                  components={{ a: LinkRenderer }}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {chat.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                </ReactMarkdown>
              </div>
              {/* the product cards the visitor saw under this answer */}
              {Array.isArray(chat.products) && chat.products.length > 0 && (
                <div className="history-products"><ProductCarousel products={chat.products} /></div>
              )}
              <span className="message-time">{formatMessageTime(chat.timestamp)}</span>
              {(hasWeb || hasDocs || hasFaq) && (
            <div className="sources-container">
              <button
                className="sources-button"
                onClick={() => handleToggleSources(sourceKey)}
              >
                {t("sources")}
                <FontAwesomeIcon
                  icon={expandedSources[sourceKey] ? faChevronUp : faChevronDown}
                  className="sources-toggle-icon"
                />
              </button>

              <div className={`sources-list ${expandedSources[sourceKey] ? "expanded" : ""}`}>
                {hasWeb && chat.sources.website_links.map((url, i) => (
                  <a key={`web-${i}`} href={url} target="_blank" rel="noopener noreferrer" className="source-link">
                    <FontAwesomeIcon icon={faGlobe} className="globe-icon" />
                    {url}
                  </a>
                ))}
                {hasDocs && chat.sources.documents.map((doc, i) => (
                  <div key={`doc-${i}`} className="source-link">
                    <FontAwesomeIcon icon={faFileAlt} className="globe-icon" />
                    {doc}
                  </div>
                ))}
                {hasFaq && chat.sources.faq.map((faq, i) => (
                  <div key={`faq-${i}`} className="source-link">
                    <FontAwesomeIcon icon={faCircleQuestion} className="globe-icon" />
                    {faq}
                  </div>
                ))}
              </div>
            </div>
              )}
            </>
          )}
        </div>
      );
    });

    return elements;
  };

  // The server already filters sessions by the search term (message content),
  // so the list renders exactly what the container loaded.
  const filteredSessions = sessions;

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const currentSession = sessions.find(s => s.id === selectedSession);
  const isSocialPlatform = currentSession && SOCIAL_PLATFORMS.includes(currentSession.platform);

  // Don't render chat view if currentSession is not found
  const showChatView = selectedSession && currentSession;

  return (
    <div className="chat-history-overview">
      {/* Left Sidebar - Chat List */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <span className={`header-title ${searchOpen ? 'hidden' : ''}`}>
            {t('conversations') || 'Conversations'}
          </span>
          <div className={`search-container ${searchOpen ? 'expanded' : ''}`}>
            {searchOpen && (
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={t('search_conversations') || 'Search...'}
                value={searchTerm}
                onChange={(e) => onSearchTermChange?.(e.target.value)}
              />
            )}
            <button
              className="search-toggle"
              onClick={() => {
                if (searchOpen && searchTerm) {
                  onSearchTermChange?.('');
                }
                setSearchOpen(!searchOpen);
              }}
            >
              <FontAwesomeIcon icon={searchOpen ? faTimes : faSearch} />
            </button>
          </div>
        </div>
        <div className="chat-list" ref={chatListRef} onScroll={handleChatListScroll}>
          {filteredSessions.length === 0 && searchTerm.trim() && (
            <div className="chat-list-empty">{t('inbox_no_search_results')}</div>
          )}
          {filteredSessions.map((session) => {
            // Preview, country and bot come precomputed on the session
            // summary (the server prefers the last visitor message, falling
            // back to the last message of any sender) — the messages
            // themselves are not loaded until the thread opens.
            const lastUserMessage = session.preview || t("no_messages_in_this_session");
            const botName = session.botName || 'Bot';
            const lastMessageTime = session.lastActivity
              ? formatListDate(session.lastActivity)
              : session.date;
            const countryCode = session.country;
            const isWebsite = !SOCIAL_PLATFORMS.includes(session.platform) && session.channel !== 'portal';

            return (
              <div
                key={session.id}
                className={`chat-list-item ${selectedSession === session.id ? 'active' : ''}`}
                onClick={() => selectSession(session.id)}
              >
                <div className={`chat-avatar ${getDisplayPlatform(session.platform, session.channel)}`}>
                  <FontAwesomeIcon
                    icon={getPlatformIcon(session.platform, session.channel)}
                    className="chat-avatar-icon"
                  />
                </div>
                <div className="chat-item-content">
                  <div className="chat-item-header">
                    <span className="chat-item-name">{getSessionName(session)}</span>
                    <span className="chat-item-time">{lastMessageTime}</span>
                  </div>
                  <div className="chat-item-preview">
                    {lastUserMessage.length > 40 ? `${lastUserMessage.slice(0, 40)}...` : lastUserMessage}
                  </div>
                  <div className="chat-item-footer">
                    <span className="chat-item-bot">
                      {botName.length > 12 ? `${botName.slice(0, 10)}...` : botName}
                    </span>
                    {SOCIAL_PLATFORMS.includes(session.platform) && autoResponseEnabled[session.id] !== undefined && (
                      <span
                        className={`auto-response-dot ${autoResponseEnabled[session.id] !== false ? 'enabled' : 'disabled'}`}
                        title={autoResponseEnabled[session.id] !== false ? t('auto_response_on') : t('auto_response_off')}
                      />
                    )}
                    {isWebsite && countryCode && (
                      <img
                        src={getCountryFlagUrl(countryCode)}
                        alt={countryCode}
                        title={countryCode}
                        className="chat-item-country-flag"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {loadingMore && (
            <div className="chat-list-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Chat View */}
      <div className="chat-main">
        {showChatView ? (
          <>
            {/* Chat Header */}
            <div className="chat-main-header">
              <div className="chat-main-header-left">
                <div className="chat-main-info">
                  <h3>{getSessionName(currentSession)}</h3>
                </div>
              </div>

              {/* Auto-response toggle for social platforms */}
              {isSocialPlatform && disconnected[selectedSession] && (
                <span className="inbox-channel-disconnected">{t('inbox_channel_disconnected')}</span>
              )}
              {isSocialPlatform && !disconnected[selectedSession] && (
                <div className="auto-response-toggle">
                  <span className="toggle-label">
                    {t('auto_response') || 'Auto-Response'}
                  </span>
                  <button
                    className={`toggle-button ${autoResponseEnabled[selectedSession] !== false ? 'enabled' : 'disabled'}`}
                    onClick={() => handleToggleAutoResponse(
                      selectedSession,
                      currentSession.platform,
                      autoResponseEnabled[selectedSession] !== false
                    )}
                    disabled={loadingAutoResponse[selectedSession]}
                  >
                    {loadingAutoResponse[selectedSession] ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon
                        icon={autoResponseEnabled[selectedSession] !== false ? faToggleOn : faToggleOff}
                      />
                    )}
                    {autoResponseEnabled[selectedSession] !== false
                      ? (t('enabled') || 'On')
                      : (t('disabled') || 'Off')}
                  </button>
                </div>
              )}

              {/* Country flag for website visitors */}
              {!isSocialPlatform && currentSession?.country && (
                <img
                  src={getCountryFlagUrl(currentSession.country, 'large')}
                  alt={currentSession.country}
                  title={currentSession.country}
                  className="chat-header-country-flag"
                />
              )}
            </div>

            {/* Chat Messages (lazy-loaded per session) */}
            {SOCIAL_PLATFORMS.includes(currentSession.platform) && <SocialDeliveryError error={deliveryErrors[selectedSession]} />}
            <div className="chat-messages-container">
              {loadingMessages[selectedSession] ? (
                <div className="chat-messages-state">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              ) : messageErrors[selectedSession] ? (
                <div className="chat-messages-state">
                  <p>{t('inbox_messages_load_failed')}</p>
                  <button
                    type="button"
                    className="inbox-retry-button"
                    onClick={() => onRetryMessages?.(selectedSession)}
                  >
                    {t('inbox_try_again')}
                  </button>
                </div>
              ) : (
                <div className="chat-messages" ref={chatMessagesRef} onScroll={handleMessagesScroll}>
                  {stickyDate && (
                    <div className="chat-sticky-date hidden" ref={stickyDateRef}>
                      <span className="chat-sticky-date-text">{formatDateHeader(stickyDate)}</span>
                    </div>
                  )}
                  {renderChatMessages()}
                </div>
              )}
            </div>

            {/* Chat Input - Only for social platforms whose channel is still connected */}
            {isSocialPlatform && !disconnected[selectedSession] && (
            <div className="chat-input-area">
                <div className="chat-input-wrapper">
                  <textarea
                    placeholder={t('type_reply') || 'Message...'}
                    value={replyText[selectedSession] || ''}
                    onChange={(e) => setReplyText(prev => ({ ...prev, [selectedSession]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !sendingMessage[selectedSession]) {
                        e.preventDefault();
                        handleSendMessage(selectedSession, currentSession.platform);
                      }
                    }}
                    disabled={sendingMessage[selectedSession]}
                    rows={1}
                  />
                </div>
                <button
                  className="send-button"
                  onClick={() => handleSendMessage(selectedSession, currentSession.platform)}
                  disabled={sendingMessage[selectedSession] || !replyText[selectedSession]?.trim()}
                >
                  {sendingMessage[selectedSession] ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
                      <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z"/>
                    </svg>
                  )}
                </button>
              </div>
            )}
            {/* Website chats: reply after the chat has ended */}
            {!isSocialPlatform && followups[selectedSession] && (() => {
              const f = followups[selectedSession];
              if (!f.available) {
                if (f.reason === 'visitor_online') {
                  return (
                    <div className="followup-note">
                      {t('followup_visitor_online')}{' '}
                      <Link to={buildLiveDeepLink({ botId: currentSession.botId, sessionId: selectedSession })}>
                        {t('followup_open_live')}
                      </Link>
                    </div>
                  );
                }
                const note = {
                  cleared: t('followup_cleared'),
                  legacy_widget: t('followup_legacy'),
                  expired: t('followup_expired'),
                }[f.reason];
                return note ? <div className="followup-note">{note}</div> : null;
              }
              return (
                <>
                  <div className="followup-note">
                    {f.pending_since
                      ? (f.last_reply?.delivered_at
                        ? t('followup_seen', { date: formatFollowupDate(f.last_reply.delivered_at) })
                        : t('followup_waiting'))
                      : t('followup_hint', { date: formatFollowupDate(f.until) })}
                  </div>
                  <div className="chat-input-area">
                    <div className="chat-input-wrapper">
                      <textarea
                        placeholder={t('followup_placeholder')}
                        value={replyText[selectedSession] || ''}
                        onChange={(e) => setReplyText(prev => ({ ...prev, [selectedSession]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !sendingMessage[selectedSession]) {
                            e.preventDefault();
                            handleSendFollowup(selectedSession);
                          }
                        }}
                        disabled={sendingMessage[selectedSession]}
                        rows={1}
                      />
                    </div>
                    <button
                      className="send-button"
                      onClick={() => handleSendFollowup(selectedSession)}
                      disabled={sendingMessage[selectedSession] || !replyText[selectedSession]?.trim()}
                    >
                      {sendingMessage[selectedSession] ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
                          <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </>
              );
            })()}
          </>
        ) : (
          <div className="chat-empty-state">
            <FontAwesomeIcon icon={faComments} />
            <h3>{t('select_conversation') || 'Select a conversation'}</h3>
            <p>{t('choose_from_list') || 'Choose from the list on the left to view messages'}</p>
          </div>
        )}
      </div>

      {/* Add FAQ Modal */}
      <AddFaqFromHistoryModal
        open={faqModalOpen}
        onClose={() => setFaqModalOpen(false)}
        question={prefilledQuestion}
        answer={prefilledAnswer}
        botId={prefilledBotId}
      />
    </div>
  );
};

export default ChatHistoryOverview;
